import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  ContactsService,
  DoctorService,
  PatientsService,
} from '../../../../../../../../core/lists/_services';
import {
  Allergy,
  PractitionRoleService,
  selectAllergiesActionLoading,
} from '../../../../../../../../core/lists';
import { select, Store } from '@ngrx/store';

import { AppState } from '../../../../../../../../core/reducers';
import {
  AllergyOnServerCreated,
  AllergyOnServerUpdated,
} from '../../../../../../../../core/lists/_actions/allergy.actions';
import { selectAllergyErrorState } from '../../../../../../../../core/lists/_selectors/allergy.selectors';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { ConditionsService } from '../../../../../../../../core/lists/_services/condition.service';

@Component({
  selector: 'kt-allergy-edit',
  templateUrl: './allergy-edit.component.html',
})
export class AllergyEditComponent implements OnInit, OnDestroy {
  // Public properties
  allergy: Allergy;
  allergyEditForm: FormGroup;
  hasFormErrors = false;
  hasSubmissionErrors = false;
  submissionErrorObj: any;
  viewLoading = true;
  loadingAfterSubmit = false;
  entityInputController;
  selectable = true;
  removable = true;
  filteredEntities: Observable<string[]>;
  entityList: string[] = [];
  @ViewChild('entityInput') entityInput: ElementRef<HTMLInputElement>;
  isCreate: boolean | boolean;
  public isReadOnly: boolean;

  filteredCode;
  @ViewChild('codeInput') codeInput: ElementRef<HTMLInputElement>;
  codeList: any = [];

  filteredExposureRoute;
  @ViewChild('exposureRouteInput') exposureRouteInput: ElementRef<HTMLInputElement>;
  exposureRouteList: any = [];

  filteredManifestation;
  @ViewChild('manifestationInput') manifestationInput: ElementRef<HTMLInputElement>;
  manifestationList: any = [];

  filteredSubstance;
  @ViewChild('substanceInput') substanceInput: ElementRef<HTMLInputElement>;
  substanceList: any = [];

  filteredRecorder;
  @ViewChild('recorderInput') recorderInput: ElementRef<HTMLInputElement>;
  recorderList: any = [];

  filteredAsserter;
  @ViewChild('asserterInput') asserterInput: ElementRef<HTMLInputElement>;
  asserterList: any = [];

  asserterSelected = false;
  private componentSubscriptions: Subscription;

  public categories: Array<any> = [
    { description: 'Food', value: 'food' },
    { description: 'Medication', value: 'medication' },
    { description: 'Environment', value: 'environment' },
    { description: 'Biologic', value: 'biologic' },
  ];
  private patientId: any;
  recorderOption = '';
  asserterOption = '';
  onsetTypeOption = '';

  constructor(
    public dialogRef: MatDialogRef<AllergyEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private store: Store<AppState>,
    private practitionRoleService: PractitionRoleService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private patientsService: PatientsService,
    private doctorsService: DoctorService,
    private conditionService: ConditionsService,
    private contactsService: ContactsService,
  ) {}

  ngOnInit() {
    this.isReadOnly = this.data.isReadOnly;
    this.allergy = this.data.allergy;
    this.patientId = this.data.entityId;
    this.store
      .pipe(select(selectAllergiesActionLoading))
      .subscribe((res) => (this.viewLoading = res));
    this.createForm();
  }

  // tslint:disable-next-line:use-life-cycle-interface
  ngOnDestroy() {
    if (this.componentSubscriptions) {
      this.componentSubscriptions.unsubscribe();
    }
  }

  createForm() {
    let onsetType = '';
    let onsetDateVal = '';
    let onsetStringVal = '';
    let onsetPeriodStartVal = '';
    let onsetPeriodEndVal = '';
    let onsetAgeVal = '';
    let onsetAgeUnitVal = '';
    let onsetRangeHighVal = '';
    let onsetRangeLowVal = '';
    const objOnset = this.allergy.onset;
    if (objOnset !== null && objOnset !== undefined) {
      const keys = Object.keys(objOnset);
      switch (keys[0]) {
        case 'dateTime': {
          onsetType = 'dateTime';
          this.onsetTypeOption = onsetType;
          onsetDateVal = this.allergy.onset['dateTime'];
          break;
        }
        case 'string': {
          onsetType = 'string';
          this.onsetTypeOption = onsetType;
          onsetStringVal = this.allergy.onset['string'];
          break;
        }
        case 'Period': {
          onsetType = 'Period';
          this.onsetTypeOption = onsetType;
          onsetPeriodStartVal = this.allergy.onset['Period']['start'];
          onsetPeriodEndVal = this.allergy.onset['Period']['end'];
          break;
        }
        case 'Age': {
          onsetType = 'Age';
          this.onsetTypeOption = onsetType;
          onsetAgeVal = this.allergy.onset['Age']['value'];
          onsetAgeUnitVal = this.allergy.onset['Age']['unit'];
          break;
        }
        case 'Range': {
          onsetType = 'Range';
          this.onsetTypeOption = onsetType;
          onsetRangeLowVal = this.allergy.onset['Range']['low']['value'];
          onsetRangeHighVal = this.allergy.onset['Range']['high']['value'];
          break;
        }
        default: {
          break;
        }
      }
    }
    this.recorderOption =
      this.allergy.recorder !== null && this.allergy.recorder !== undefined
        ? this.allergy.recorder.resourceType
        : '';

    this.asserterOption =
      this.allergy.asserter !== null && this.allergy.asserter !== undefined
        ? this.allergy.asserter.resourceType
        : '';

    if (
      this.allergy &&
      this.allergy.recorder &&
      this.allergy.recorder['id'] &&
      this.allergy.recorder['resourceType']
    ) {
      this.conditionService
        .getResourceById(this.allergy.recorder['resourceType'], this.allergy.recorder['id'])
        .subscribe((res) => {
          if (res) {
            this.allergyEditForm.controls.recorder.setValue(res);
          }
        });
    }

    if (
      this.allergy &&
      this.allergy.asserter &&
      this.allergy.asserter['id'] &&
      this.allergy.asserter['resourceType']
    ) {
      this.conditionService
        .getResourceById(this.allergy.asserter['resourceType'], this.allergy.asserter['id'])
        .subscribe((res) => {
          if (res) {
            this.allergyEditForm.controls.asserter.setValue(res);
          }
        });
    }
    this.allergyEditForm = this.fb.group({
      code: ['', Validators.required],

      clinicalStatus: [
        this.allergy.id && this.allergy.clinicalStatus !== null
          ? this.allergy.clinicalStatus.coding[0].code
          : null,
      ],
      verificationStatus: [
        this.allergy.id && this.allergy.verificationStatus !== null
          ? this.allergy.verificationStatus.coding[0].code
          : null,
      ],

      type: [this.allergy.type],
      criticality: [this.allergy.criticality],
      onset_type: [onsetType],
      onsetDate: [onsetDateVal],
      onsetString: [onsetStringVal],
      onsetPeriodStart: [onsetPeriodStartVal],
      onsetPeriodEnd: [onsetPeriodEndVal],
      onsetAge: [onsetAgeVal],
      onsetAgeUnit: [onsetAgeUnitVal],
      onsetRangeHigh: [onsetRangeHighVal],
      onsetRangeLow: [onsetRangeLowVal],

      checkArray: this.fb.array([]),

      recordedDate: [
        this.allergy.recordedDate !== null && this.allergy.recordedDate !== ''
          ? new Date(this.allergy.recordedDate)
          : '',
      ],
      lastOccurrence: [
        this.allergy.lastOccurrence !== null && this.allergy.lastOccurrence !== ''
          ? new Date(this.allergy.lastOccurrence)
          : '',
      ],

      note: [
        this.allergy.note !== null && this.allergy.note !== undefined
          ? this.allergy.note[0].text
          : '',
        Validators.required,
      ],
      manifestation: [],
      description: [],
      manifestationOnset: [],
      severity: [],
      exposureRoute: [],
      recorder: [],
      asserter: [],
      substance: [],
      recorderResourceType: [
        this.allergy.recorder !== null && this.allergy.recorder !== undefined
          ? this.allergy.recorder.resourceType
          : '',
      ],
      asserterType: [
        this.allergy.asserter !== null && this.allergy.asserter !== undefined
          ? this.allergy.asserter.resourceType
          : '',
      ],
    });
    if (this.isReadOnly) {
      this.allergyEditForm.disable();
    }
  }

  prepareAllergy(): Allergy {
    const controls = this.allergyEditForm.controls;
    const _allergy = new Allergy();
    _allergy.clear();
    _allergy.id = this.allergy.id;
    _allergy.code = controls.code.value;
    _allergy.clinicalStatus = controls.clinicalStatus.value;
    _allergy.verificationStatus = controls.verificationStatus.value;
    _allergy.recordedDate = this.datePipe.transform(controls.recordedDate.value, 'yyyy-MM-dd');
    _allergy.lastOccurrence = this.datePipe.transform(controls.lastOccurrence.value, 'yyyy-MM-dd');
    _allergy.note = controls.note.value;
    _allergy.type = controls.type.value;
    _allergy.criticality = controls.criticality.value;
    _allergy.category = controls.checkArray.value;
    _allergy.substance = controls.substance.value;
    _allergy.manifestation = controls.manifestation.value;
    _allergy.manifestationDescription = controls.description.value;
    _allergy.manifestationOnset = this.datePipe.transform(
      controls.manifestationOnset.value,
      'yyyy-MM-dd',
    );
    _allergy.manifestationSeverity = controls.severity.value;
    _allergy.exposureRoute = controls.exposureRoute.value;
    _allergy.recorderResourceType = controls.recorderResourceType.value;
    _allergy.recorder = controls.recorder.value;
    _allergy.onsetType = controls.onset_type.value;
    _allergy.onsetDate = this.datePipe.transform(controls.onsetDate.value, 'yyyy-MM-dd');
    _allergy.onsetString = controls.onsetString.value;
    _allergy.onsetPeriodStart = this.datePipe.transform(
      controls.onsetPeriodStart.value,
      'yyyy-MM-dd',
    );
    _allergy.onsetPeriodEnd = this.datePipe.transform(controls.onsetPeriodEnd.value, 'yyyy-MM-dd');
    _allergy.onsetAge = controls.onsetAge.value;
    _allergy.onsetAgeUnit = controls.onsetAgeUnit.value;
    _allergy.onsetRangeLow = controls.onsetRangeLow.value;
    _allergy.onsetRangeHigh = controls.onsetRangeHigh.value;
    _allergy.asserter = controls.asserter.value;
    _allergy.asserterType = controls.asserterType.value;
    return _allergy;
  }

  onSubmit() {
    this.hasFormErrors = false;
    this.hasSubmissionErrors = false;
    const controls = this.allergyEditForm.controls;
    /** check form */
    if (this.allergyEditForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());

      this.hasFormErrors = true;
      return;
    }

    const editedAllergy = this.prepareAllergy();
    if (editedAllergy.id !== 0) {
      this.updateAllergy(editedAllergy);
    } else {
      this.createAllergy(editedAllergy);
    }
  }

  updateAllergy(_allergy: Allergy) {
    this.store.dispatch(
      new AllergyOnServerUpdated({
        allergy: _allergy,
        entityId: this.patientId,
      }),
    );
    this.store.pipe(select(selectAllergiesActionLoading)).subscribe((res) => {
      if (res !== true) {
        this.store.pipe(select(selectAllergyErrorState)).subscribe((error) => {
          if (error) {
            if (error.error.errors) {
              this.submissionErrorObj = error.error.errors;
            } else {
              this.submissionErrorObj = [{ message: 'GLOBAL.UNSPECIFIED_ERROR' }];
            }
            this.hasSubmissionErrors = true;
          } else {
            this.dialogRef.close({ _allergy, isEdit: true });
          }
        });
      }
    });
  }

  createAllergy(_allergy: Allergy) {
    this.store.dispatch(
      new AllergyOnServerCreated({ allergy: _allergy, entityId: this.patientId }),
    );
    this.store.pipe(select(selectAllergiesActionLoading)).subscribe((res) => {
      if (res !== true) {
        this.store.pipe(select(selectAllergyErrorState)).subscribe((error) => {
          if (error) {
            if (error.error.errors) {
              this.submissionErrorObj = error.error.errors;
            } else {
              this.submissionErrorObj = [{ message: 'GLOBAL.UNSPECIFIED_ERROR' }];
            }
            this.hasSubmissionErrors = true;
          } else {
            this.dialogRef.close({ _allergy, isEdit: false });
          }
        });
      }
    });
  }

  onAlertClose() {
    this.hasFormErrors = false;
  }

  getTitle() {
    let result = this.translate.instant('ALLERGY.NEW_ALLERGY');
    if (!this.allergy.id) {
      return result;
    }
    if (this.isReadOnly) {
      result = this.translate.instant('ALLERGY.VIEW_ALLERGY') + `-` + `${this.allergy.id}`;
      return result;
    }

    result = this.translate.instant('ALLERGY.EDIT_ALLERGY') + `-` + `${this.allergy.id}`;
    return result;
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.allergyEditForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  selectedCode(event: MatAutocompleteSelectedEvent): void {
    this.codeList.push(event.option.value);
    this.codeInput.nativeElement.value = event.option.value.concept.fsn.term;
  }

  codeInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredCode = this.practitionRoleService.searchCode(value).pipe(
          map((res) =>
            res.items.filter((r) => {
              return !this.codeList.find((e: any) => e.concept.id === r.concept.id);
            }),
          ),
        );
    } else {
      this.filteredCode = of([]);
    }
  }

  displayFnCode(event: any): string {
    return event && event.concept.fsn.term ? event.concept.fsn.term : '';
  }

  selectedSubstance(event: MatAutocompleteSelectedEvent): void {
    this.substanceList.push(event.option.value);
    this.substanceInput.nativeElement.value = event.option.value.concept.fsn.term;
  }

  substanceInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredSubstance = this.practitionRoleService.searchCode(value).pipe(
          map((res) =>
            res.items.filter((r) => {
              return !this.substanceList.find((e: any) => e.concept.id === r.concept.id);
            }),
          ),
        );
    } else {
      this.filteredSubstance = of([]);
    }
  }

  displayFnSubstance(event: any): string {
    return event && event.concept.fsn.term ? event.concept.fsn.term : '';
  }

  selectedManifestation(event: MatAutocompleteSelectedEvent): void {
    this.manifestationList.push(event.option.value);
    this.manifestationInput.nativeElement.value = event.option.value.concept.fsn.term;
  }

  manifestationInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredManifestation = this.practitionRoleService.searchCode(value).pipe(
          map((res) =>
            res.items.filter((r) => {
              return !this.manifestationList.find((e: any) => e.concept.id === r.concept.id);
            }),
          ),
        );
    } else {
      this.filteredManifestation = of([]);
    }
  }

  displayFnManifestation(event: any): string {
    return event && event.concept.fsn.term ? event.concept.fsn.term : '';
  }

  selectedExposureRoute(event: MatAutocompleteSelectedEvent): void {
    this.exposureRouteList.push(event.option.value);
    this.exposureRouteInput.nativeElement.value = event.option.value.concept.fsn.term;
  }

  exposureRouteInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredExposureRoute = this.practitionRoleService.searchCode(value).pipe(
          map((res) =>
            res.items.filter((r) => {
              return !this.exposureRouteList.find((e: any) => e.concept.id === r.concept.id);
            }),
          ),
        );
    } else {
      this.filteredExposureRoute = of([]);
    }
  }

  displayFnExposureRoute(event: any): string {
    return event && event.concept.fsn.term ? event.concept.fsn.term : '';
  }

  onCatChange(e) {
    const checkArray: FormArray = this.allergyEditForm.get('checkArray') as FormArray;

    if (e.target.checked) {
      checkArray.push(new FormControl(e.target.value));
    } else {
      let i = 0;
      checkArray.controls.forEach((item: FormControl) => {
        if (item.value === e.target.value) {
          checkArray.removeAt(i);
          return;
        }
        i++;
      });
    }
  }

  selectedRecorder(event: MatAutocompleteSelectedEvent): void {
    this.recorderList.push(event.option.value);
    if (this.recorderOption === 'RelatedPerson') {
      this.recorderInput.nativeElement.value =
        event.option.value && event.option.value.contact
          ? event.option.value.contact.first_name
          : null;
      return;
    }
    this.recorderInput.nativeElement.value = event.option.value.first_name;
  }

  recorderInputChanged(value, typeOfSelected) {
    if (
      value &&
      (typeOfSelected === 'Patient' ||
        typeOfSelected === 'Practitioner' ||
        typeOfSelected === 'RelatedPerson')
    ) {
      if (typeof value === 'string' && typeOfSelected === 'Patient')
        this.filteredRecorder = this.patientsService.searchPatients(value).pipe(
          map((res) =>
            res.data.filter((r) => {
              return !this.recorderList.find((e: any) => e.id === r.id);
            }),
          ),
        );
      if (typeof value === 'string' && typeOfSelected === 'Practitioner')
        this.filteredRecorder = this.doctorsService.searchDoctors(value).pipe(
          map((res) =>
            res.data.filter((r) => {
              return !this.recorderList.find((e: any) => e.id === r.id);
            }),
          ),
        );
      if (typeof value === 'string' && typeOfSelected === 'RelatedPerson')
        this.filteredRecorder = this.contactsService
          .searchRelatedPerson(value, this.patientId)
          .pipe(
            map((res) =>
              res.data.filter((r) => {
                return !this.recorderList.find((e: any) => e.id === r.id);
              }),
            ),
          );
    } else {
      this.filteredRecorder = of([]);
    }
  }

  displayFnRecorder(event: any): string {
    if (this.recorderOption === 'RelatedPerson') {
      return event && event['patient'] ? event['patient']['resourceType'] : null;
    }
    return event && event['name'] && event['name'][0] && event['name'][0]['given']
      ? event['name'][0]['given'][0]
      : null;
  }

  choseRecorderType(value) {
    this.recorderOption = value;
    this.recorderList = [];
    this.filteredRecorder = of([]);
    if (this.recorderInput) this.recorderInput.nativeElement.value = null;
  }

  chooseOnsetType(value) {
    this.onsetTypeOption = value;
    // this.recorderList = [];
    // this.recorderInput.nativeElement.value = null;
  }

  selectedAsserter(event: MatAutocompleteSelectedEvent): void {
    this.asserterList.push(event.option.value);
    if (this.asserterOption === 'RelatedPerson') {
      this.asserterInput.nativeElement.value =
        event.option.value && event.option.value.contact
          ? event.option.value.contact.first_name
          : null;
      return;
    }
    this.asserterInput.nativeElement.value = event.option.value.first_name;
  }

  asserterInputChanged(value, typeOfSelected) {
    if (value) {
      if (typeof value === 'string' && typeOfSelected === 'Patient')
        this.filteredAsserter = this.patientsService.searchPatients(value).pipe(
          map((res) =>
            res.data.filter((r) => {
              return !this.asserterList.find((e: any) => e.id === r.id);
            }),
          ),
        );
      if (typeof value === 'string' && typeOfSelected === 'Practitioner')
        this.filteredAsserter = this.doctorsService.searchDoctors(value).pipe(
          map((res) =>
            res.data.filter((r) => {
              return !this.asserterList.find((e: any) => e.id === r.id);
            }),
          ),
        );
      if (typeof value === 'string' && typeOfSelected === 'RelatedPerson')
        this.filteredAsserter = this.contactsService
          .searchRelatedPerson(value, this.patientId)
          .pipe(
            map((res) =>
              res.data.filter((r) => {
                return !this.asserterList.find((e: any) => e.id === r.id);
              }),
            ),
          );
    } else {
      this.filteredAsserter = of([]);
    }
  }

  displayFnAsserter(event: any): string {
    if (this.asserterOption === 'RelatedPerson') {
      return event && event['patient'] ? event['patient']['resourceType'] : null;
    }
    return event && event['name'] && event['name'][0] && event['name'][0]['given']
      ? event['name'][0]['given'][0]
      : null;
  }

  chooseAsserterType(value) {
    this.asserterOption = value;
    this.asserterList = [];
    this.filteredAsserter = of([]);
    if (this.asserterInput) this.asserterInput.nativeElement.value = null;
  }

  getName(obj, type) {
    if (type === 'RelatedPerson' && obj.contact) {
      return obj.contact.first_name + ' ' + obj.contact.last_name;
    }
    return obj.first_name + ' ' + obj.last_name;
  }
}
